import { apiClient } from '../client';
import { dataServiceClient } from '../dataservice-client';

/** One action inside a connector's catalog entry — GET /connector/{id}.actions[]. */
export interface ConnectorCatalogAction {
  name: string;         // human display name, e.g. "Create Comment"
  sort: string;          // internal id, e.g. "create_comment" — sort + "." + category === ActionDef.name
  category: string;      // e.g. "linear"
  endpoint: string;
  description: string;
  is_enabled: boolean;
}

/** GET /connector/{id} — the full connector catalog record (data-service, direct via /data-api). */
export interface ConnectorCatalogDetail {
  _id: string;
  name: string;
  description: string;
  category?: string;
  readme_b64?: string;
  logo?: string;
  actions: ConnectorCatalogAction[];
}

/** One input/output field of a connector action, as declared by the action's own code. */
export interface ConnectorFieldSchema {
  field_id: string;
  label?: string;
  type?: string;
  required?: boolean;
  description?: string;
  [key: string]: unknown;
}

export interface ConnectorSchemaShape {
  fields?: ConnectorFieldSchema[];
}

/** One action in GET /connector/schema/{category}. `tool_name` is the action's `sort` id. */
export interface ConnectorActionSchema {
  name: string;
  description: string;
  tool_name: string;
  endpoint: string;
  source_type: 'action' | 'trigger' | 'data_loader' | string;
  input_schema: ConnectorSchemaShape;
  output_schema: ConnectorSchemaShape;
}

/** GET /connector/schema/{category}?include_readme=true — catalog docs, no connection needed. */
export interface ConnectorDocs {
  category: string;
  name: string;
  description: string;
  logo?: string;
  readme_b64?: string | null;
  actions: ConnectorActionSchema[];
}

export interface DataServiceProject {
  _id: string;
  name: string;
  description?: string;
  role?: string;
  projectID?: string;
  organization_id?: string;
  image?: string;
  isActive: boolean;
  is_deleted: boolean;
  created_at?: number;
  updated_at?: number;
  metadata?: Record<string, string>;
}

export interface ProjectListResponse {
  agentflows: DataServiceProject[];
  total_count: number;
  skip: number;
  limit: number;
}

export interface ProjectConnector {
  _id: string;
  projectID: string;
  category: string;
  // Functional grouping (e.g. "crm", "communication") — distinct from
  // category above, which is the connector-type slug (e.g. "linear").
  connector_group_tag?: string;
  /** data-service's classification: `user` | `system` | `external`. `system`
   *  connectors are internal to the platform and are never listed to users. */
  connector_type?: string;
  name: string;
  status: string;
  logo?: string;
  description?: string;
  config?: Record<string, any>;
  data_to_verify?: Record<string, any>;
  triggers?: any[];
  accountDetails?: Array<{ connectorID?: string; [key: string]: any }>;
  is_deleted: boolean;
  created_at?: any;
  updated_at?: any;
}

export interface ProjectConnectorsResponse {
  connectors: ProjectConnector[];
  total: number;
  projectId: string;
}

export interface AvailableConnector {
  _id?: string;
  name: string;
  category?: string;
  /** data-service's classification: `user` | `system` | `external`. */
  connector_type?: string;
  description?: string;
  logo?: string;
  actions?: any[];
  tags?: string[];
  is_starred?: boolean;
  is_premium?: boolean;
  ui_form?: UIFormField[];
  meta_data?: {
    auth_url?: string;
    has_auth?: boolean;
    auth_provider?: string;
    auth_variables?: string[];
    auth_button_title?: string;
  };
  [key: string]: any;
}

export interface UIFormField {
  field_id: string;
  label: string;
  type: string;
  description?: string;
  required?: boolean;
  /** Show this field only while another field holds one of these values. */
  visible_when?: Record<string, string | string[]>;
  placeholder?: string;
  default_value?: any;
  options?: { label: string; value: string }[];
  /** Grouped under the wizard's collapsible "Advanced Config" section (OM-parity). */
  advanced?: boolean;
  /** Secret fields stay blank on edit ("leave empty to keep current"). */
  is_secret?: boolean;
}

/** One OM connection serviceType within a category (e.g. Snowflake → databaseServices). */
export interface OMCatalogType {
  service_type: string;
  path: string;
}

/** An OM service category (Databases, APIs, Messaging, …) and its serviceTypes. */
export interface OMCatalogCategory {
  label: string;
  path: string;
  types: OMCatalogType[];
}

export interface ConnectorConfigFormResponse {
  config: {
    _id?: string;
    name: string;
    category: string;
    description?: string;
    logo?: string;
    ui_form?: UIFormField[];
    actions?: any[];
    data_loaders?: any[];
    triggers?: any[];
    available_events?: any[];
    meta_data?: {
      auth_url?: string;
      has_auth?: boolean;
      auth_provider?: string;
      auth_variables?: string[];
      auth_button_title?: string;
    };
    [key: string]: any;
  };
}

/** Access scope of a connected connector — who can see/use it. */
export type ConnectorAccessScope = 'organization' | 'department' | 'project' | 'individual';

/** Role-bounded scope options the connect wizard may offer (server-resolved). */
export interface ConnectorAccessScopesResponse {
  project_id: string;
  allowed_scopes: ConnectorAccessScope[];
  default_scope: ConnectorAccessScope;
  /** The active project's department — present only when it belongs to one. */
  department_id?: string;
  /** Set when the caller's role would allow Department but the project has none. */
  department_unavailable_reason?: string;
  role?: string;
}

/**
 * CONSENT, deliberately separate from the visibility `access_scope` grants:
 * which projects the connector's OWNER admits for attaching triggers.
 *
 * Seeing a connector must not imply being able to drive it. An action call is
 * made once, by a person or a run; a trigger is fired by the PROVIDER,
 * unattended, for as long as it exists, and spends the owner's third-party
 * credential and rate limit every time. `home_only` is the default and how an
 * absent value reads.
 */
export type ConnectorTriggerPolicy = 'home_only' | 'allow_visible' | 'allowlist';

export interface ConnectorTriggerPolicyResponse {
  connector_id: string;
  trigger_policy: ConnectorTriggerPolicy;
  /** Projects admitted under `allowlist`. */
  allowed_projects: string[];
  home_project_id: string;
  /** Whether the ACTIVE project may attach triggers right now. */
  admits_this_project: boolean;
  /**
   * Policies this caller may assign. Empty when they can see the connector but
   * not govern it — the UI shows the setting read-only rather than hiding it,
   * so it is obvious who can change it instead of looking like a missing
   * feature.
   */
  assignable_policies: ConnectorTriggerPolicy[];
}

export interface ConnectorConfigPayload {
  name: string;
  projectID: string;
  category?: string;
  /** Requested access scope; harness re-validates against the caller's role. */
  access_scope?: ConnectorAccessScope;
  config: Record<string, any>;
  actions?: any[];
  tags?: string[];
  triggers?: any[];
  data_loader_config?: {
    frequency?: string;
    syncInterval?: string;
  };
  data_loaders?: any[];
  ui_form?: UIFormField[];
  description?: string;
  logo?: string;
  meta_data?: any;
  [key: string]: any;
}

/**
 * Per-connector incremental polling interval (data-service
 * GET/PUT /connectors/{id}/polling-interval). `interval_seconds` is the
 * connector's own value; null means the organization plan's default applies.
 */
export interface ConnectorPollingInterval {
  connector_id: string;
  category: string;
  supported: boolean;
  min_seconds: number;
  max_seconds: number;
  interval_seconds?: number | null;
  effective_seconds?: number;
  source?: 'connector' | 'plan';
  plan?: string;
  plan_default_seconds?: number;
  schedule_status?: 'active' | 'paused' | 'not_found' | 'unknown';
  next_run_at?: string | null;
  schedule_interval_seconds?: number | null;
}

export interface ConnectorConfigResponse {
  success?: boolean;
  message?: string;
  connector_id?: string;
  data?: any;
  data_to_verify?: {
    redirect_to_configuration?: boolean;
    url?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export interface AvailableConnectorsResponse {
  data: AvailableConnector[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface SyncConnectorsResponse {
  message: string;
  projectId: string;
  synced?: number;
  syncedActions?: number;
  total?: number;
  errors?: string[];
  workflowId?: string;
  status?: string;
  async?: boolean;
}

export interface SyncStatusResponse {
  workflowId: string;
  state: {
    status: string;
    progress: number;
    currentConnector: string;
    totalConnectors: number;
    syncedConnectors: number;
    syncedActions: number;
    failedConnectors: string[];
    lastError?: string;
  };
}

export interface ConnectorSyncedStatusResponse {
  connectorId: string;
  synced: boolean;
  status: string;
  name?: string;
  category?: string;
  actionCount?: number;
  lastSync?: string;
  hasOntology?: boolean;
}

export interface SyncedConnector {
  id: string;
  name: string;
  description: string;
  category: string;
  logo: string;
  status: string;
  /** Real sync lifecycle: pending | syncing | ready | synced | failed. */
  syncStatus?: string;
  /** True only once the sync workflow has finished (ready/synced). */
  synced?: boolean;
  /** Number of actions ingested so far. */
  actionCount?: number;
  /** Last pull: dispatched | running | completed | failed | not_applicable | not_supported. */
  ingestionStatus?: string;
  ingestionError?: string;
  ingestionUpdatedAt?: string | null;
  bulkIngestionCompleted?: boolean;
  bulkIngestionCompletedAt?: string | null;
  project_id: string;
  organization_id: string;
  last_sync: string;
  created_at: string;
  updated_at: string;
}

export interface SyncedConnectorsResponse {
  connectors: SyncedConnector[];
  total: number;
  projectId: string;
}

export interface ConnectorAction {
  id: string;
  name: string;
  description: string;
  action_type: string;
  tool_name: string;
  connector_id: string;
  complexity: string;
  estimated_time: number;
  success_rate: number;
  usage_count: number;
}

export interface ConnectorActionsResponse {
  actions: ConnectorAction[];
  total: number;
  connectorId: string;
}

export interface ConnectorStats {
  total_connectors: number;
  active_connectors: number;
  total_actions: number;
}

export interface ActionSchemaField {
  field_id: string;
  label: string;
  type: string;
  description: string;
  required: boolean;
}

export interface ActionSchema {
  fields: ActionSchemaField[];
}

export interface ParameterMapping {
  param_name: string;
  source_node_type: string;
  source_property: string;
  requires_action: string;
  mapping_type: string;
  description?: string;
}

export interface EnhancedAction {
  id: string;
  name: string;
  description: string;
  action_type: string;
  action_direction: string;
  tool_name: string;
  connector_id: string;
  project_id: string;
  enabled: boolean;
  complexity: string;
  estimated_time: number;
  success_rate: number;
  usage_count: number;
  input_schema?: ActionSchema;
  output_schema?: ActionSchema;
  requires_params?: boolean;
  parameter_mappings?: ParameterMapping[];
  created_at: string;
  updated_at: string;
}

export interface ConnectorDataProperty {
  name: string;
  type: string;
  description: string;
  required: boolean;
  unique?: boolean;
  indexed?: boolean;
  format?: string;
  example?: any;
}

/** Connector-declared access-scope rule for a node type (harness scope.NodeRule).
 *  field_classes: property name -> required Level-2 code(s), as a single string
 *  ("detail:pii" | "detail:financial" | "detail:internal" | "conn:<category>:read").
 *  Overrides the generic name-heuristic classifier; absent/empty = heuristic only.
 *  detail:internal is the ABSOLUTE mask — hidden from every role, including
 *  super_admin, with no exceptions (see harness scope.FieldVisible). */
export interface ConnectorNodeScope {
  container_code?: string;
  restrict_when?: unknown[];
  container_refs?: string[];
  participant_refs?: unknown[];
  field_classes?: Record<string, string>;
  identity_fields?: string[];
}

export interface ConnectorDataNode {
  name: string;
  label: string;
  description: string;
  category: string;
  properties: ConnectorDataProperty[];
  primary_key: string;
  display_name_property: string;
  timestamp_field?: string;
  source_actions: string[];
  is_core: boolean;
  scope?: ConnectorNodeScope;
}

export interface ConnectorRelationshipProperty {
  name: string;
  type: string;
  description: string;
}

export interface ConnectorDataRelationship {
  name: string;
  label: string;
  description: string;
  from_node: string;
  to_node: string;
  cardinality: string;
  required: boolean;
  temporal?: boolean;
  properties?: ConnectorRelationshipProperty[];
  inverse_of?: string;
  semantic_type: string;
  source_actions?: string[];
}

export interface ConnectorDataSchema {
  version: string;
  connector_type: string;
  domain: string;
  nodes: ConnectorDataNode[];
  relationships: ConnectorDataRelationship[];
  core_entities: string[];
  entry_points: string[];
  summary: string;
  use_cases: string[];
}

export interface ConnectorDetail {
  id: string;
  name: string;
  description: string;
  connector_type: string;
  category: string;
  logo: string;
  url: string;
  version: string;
  status: string;
  project_id: string;
  organization_id: string;
  config: Record<string, any>;
  metadata: Record<string, any>;
  data_schema?: ConnectorDataSchema;
  last_sync: string;
  created_at: string;
  updated_at: string;
}

export interface ConnectorDetailsResponse {
  connector: ConnectorDetail;
  all_actions: EnhancedAction[];
  push_actions: EnhancedAction[];
  pull_actions: EnhancedAction[];
  other_actions: EnhancedAction[];
}

// ── Connector pipeline trace (SPEC §12) ────────────────────────────────────────
export type ConnectorPipelineStageStatus = 'done' | 'current' | 'failed' | 'pending';

export interface ConnectorPipelineStage {
  stage: string;
  status: ConnectorPipelineStageStatus;
  started_at?: string | null;
  ended_at?: string | null;
}

export interface ConnectorPipeline {
  state: string;
  label: string;
  stages: ConnectorPipelineStage[];
  ingestion_in_progress: boolean;
  last_om_ingest_at?: string | null;
  last_om_ingest_result?: Record<string, unknown> | null;
  // The ingest workflow's own disposition. 'closed' with no result means the run died
  // before recording one — reported as failed, not as still running.
  run_status?: 'running' | 'closed' | null;
  // Normalized landed counts from the last run — {nodes, edges} — for the progress counter.
  counts?: { nodes: number; edges: number };
  connector_id: string;
  category?: string;
  project_id?: string;
  org_id?: string;
  workflow_id?: string;
}

export const dataServiceApi = {
  // Get projects (agentflows) from data-service
  getProjects: (projectID: string, skip = 1, limit = 50) =>
    apiClient.get<ProjectListResponse>(
      `/api/v1/dataservice/projects?projectID=${projectID}&skip=${skip}&limit=${limit}`
    ),

  // Get connectors for a specific project
  getProjectConnectors: (projectId: string, limit = 100, skip = 0, searchKey?: string) => {
    let url = `/api/v1/dataservice/projects/${projectId}/connectors?limit=${limit}&skip=${skip}`;
    if (searchKey) {
      url += `&search_key=${encodeURIComponent(searchKey)}`;
    }
    return apiClient.get<ProjectConnectorsResponse>(url);
  },

  // Get all available connectors (catalog)
  getAvailableConnectors: (page = 1, limit = 50, projectID?: string, searchKey?: string, mcpOnly?: boolean) => {
    let url = `/api/v1/dataservice/connectors/available?page=${page}&limit=${limit}`;
    if (projectID) {
      url += `&projectID=${encodeURIComponent(projectID)}`;
    }
    if (searchKey) {
      url += `&search_key=${encodeURIComponent(searchKey)}`;
    }
    if (mcpOnly) {
      url += `&mcp_only=true`;
    }
    return apiClient.get<AvailableConnectorsResponse>(url);
  },

  // Sync connectors from data-service to Neo4j
  syncProjectConnectors: (projectId: string) =>
    apiClient.post<SyncConnectorsResponse>(
      `/api/v1/dataservice/projects/${projectId}/connectors/sync`,
      {}
    ),

  // Regenerate ontology for a connector (deletes existing, re-syncs with fresh ontology).
  // Connector types with a Fabric-curated ontology reset to it instead (ontologyService).
  regenerateConnectorOntology: (projectId: string, connectorId: string) =>
    apiClient.post<SyncConnectorsResponse>(
      `/api/v1/dataservice/projects/${projectId}/connectors/${connectorId}/regenerate-ontology`,
      {}
    ),

  // Get synced connectors from Neo4j
  getSyncedConnectors: (projectId: string) =>
    apiClient.get<SyncedConnectorsResponse>(
      `/api/v1/dataservice/projects/${projectId}/connectors/synced`
    ),

  // Get sync workflow status
  getSyncStatus: (workflowId: string) =>
    apiClient.get<SyncStatusResponse>(
      `/api/v1/dataservice/sync/${workflowId}/status`
    ),

  // Sync a single connector
  syncSingleConnector: (projectId: string, connectorId: string) =>
    apiClient.post<SyncConnectorsResponse>(
      `/api/v1/dataservice/projects/${projectId}/connectors/${connectorId}/sync`,
      {}
    ),

  // Check if a connector is synced to Neo4j
  getConnectorSyncedStatus: (connectorId: string) =>
    apiClient.get<ConnectorSyncedStatusResponse>(
      `/api/v1/dataservice/connectors/${connectorId}/synced`
    ),

  // Per-connector pipeline trace (connection → ingestion) — SPEC §12. Read-only,
  // session-scoped; state derives from the connector lifecycle + Temporal history.
  getConnectorPipeline: (connectorId: string) =>
    apiClient.get<ConnectorPipeline>(
      `/api/v1/dataservice/connectors/${connectorId}/pipeline`
    ),

  // Start a connector's ingestion run (Stage-A ontology + pipeline) from the UI, so
  // "Connect" auto-runs it — the user never hunts for a Sync button. Returns the
  // om-ingest status ({status, workflow_id, ...}). Best-effort; the trace shows progress.
  triggerConnectorIngest: (connectorId: string) =>
    apiClient.post<{ status?: string; workflow_id?: string; message?: string }>(
      `/api/v1/dataservice/connectors/${connectorId}/ingest-run`,
      {}
    ),

  // Get actions for a specific connector (Neo4j primary, catalog fallback via ?category)
  getConnectorActions: (connectorId: string, category?: string) =>
    apiClient.get<ConnectorActionsResponse>(
      `/api/v1/dataservice/connectors/${connectorId}/actions${category ? `?category=${encodeURIComponent(category)}` : ''}`
    ),

  // Get connector statistics
  getConnectorStats: (projectId?: string) => {
    let url = `/api/v1/dataservice/connectors/stats`;
    if (projectId) {
      url += `?projectId=${encodeURIComponent(projectId)}`;
    }
    return apiClient.get<ConnectorStats>(url);
  },

  // Get detailed connector information including actions and schema
  getConnectorDetails: (connectorId: string) =>
    apiClient.get<ConnectorDetailsResponse>(
      `/api/v1/dataservice/connectors/${connectorId}/details`
    ),

  // Update action enabled/disabled status
  updateActionEnabled: (actionId: string, enabled: boolean) =>
    apiClient.put<{ success: boolean; action_id: string; enabled: boolean; message: string }>(
      `/api/v1/dataservice/actions/${actionId}/enabled`,
      { enabled }
    ),

  // Role-bounded access-scope options for the connect wizard. The server computes
  // these from the caller's RESOLVED role for this project (same matrix the create
  // endpoint enforces), so the UI can never offer a scope the save would refuse:
  // admin → organization/department/project/individual, dept admin → department/
  // project/individual, project admin → project/individual, member → individual.
  getConnectorAccessScopes: (projectId: string) =>
    apiClient.get<ConnectorAccessScopesResponse>(
      `/api/v1/dataservice/projects/${projectId}/connectors/access-scopes`
    ),

  /**
   * The connector's trigger CONSENT setting, plus whether the active project is
   * admitted and which policies this caller may assign. Distinct from
   * access-scopes above: that answers "who may see this connector", this
   * answers "whose project may attach triggers to it".
   */
  getConnectorTriggerPolicy: (projectId: string, connectorId: string) =>
    apiClient.get<ConnectorTriggerPolicyResponse>(
      `/api/v1/dataservice/projects/${projectId}/connectors/${connectorId}/trigger-policy`
    ),

  /** Set the trigger policy. Harness re-validates against the caller's role and
   *  refuses allowlisted projects that cannot even see the connector. */
  setConnectorTriggerPolicy: (
    projectId: string,
    connectorId: string,
    body: { trigger_policy: ConnectorTriggerPolicy; allowed_projects?: string[] },
  ) =>
    apiClient.put<ProjectConnector>(
      `/api/v1/dataservice/projects/${projectId}/connectors/${connectorId}/trigger-policy`,
      body,
    ),

  // Get connector configuration form schema
  getConnectorConfigForm: (projectId: string, category: string) =>
    apiClient.get<ConnectorConfigFormResponse>(
      `/api/v1/dataservice/projects/${projectId}/connectors/config/${encodeURIComponent(category)}`
    ),

  // Create a new connector configuration. Pass `connectorId` to re-run the same
  // wizard flow against an existing instance — the only path that can issue a
  // fresh hosted-auth URL for a connector stuck on "Awaiting authentication"
  // (the generic updateConnectorConfig below just rewrites fields).
  createConnectorConfig: (
    projectId: string,
    category: string,
    config: ConnectorConfigPayload,
    connectorId?: string,
  ) =>
    apiClient.post<ConnectorConfigResponse>(
      `/api/v1/dataservice/projects/${projectId}/connectors/config/${encodeURIComponent(category)}` +
        (connectorId ? `?connectorID=${encodeURIComponent(connectorId)}` : ''),
      config
    ),

  /** Load an existing connector via GET /connector/{id} (config + triggers + ui_form). */
  getConnectorInstance: (projectId: string, connectorId: string, _category?: string) =>
    apiClient.get<ProjectConnector & {
      available_events?: Array<{ name: string; description?: string; event: string }>;
      ui_form?: UIFormField[];
      files?: unknown[];
      data_loader_config?: { frequency?: string; sync_interval?: string; syncInterval?: string };
    }>(
      `/api/v1/dataservice/projects/${projectId}/connectors/${connectorId}/config`
    ),

  /**
   * Full connector catalog detail (description, readme_b64, per-action
   * description) — DIRECT to data-service via /data-api, not the harness
   * proxy (harness's own connector sync never carries readme_b64 at all, so
   * this can't be reached through it). Used to give the Simulation & Dry Run
   * form real per-connector/per-action context instead of just the thin
   * capabilityDeclaration string.
   */
  getConnectorDetail: (connectorId: string) =>
    dataServiceClient.get<ConnectorCatalogDetail>(`/connector/${connectorId}`),

  /**
   * Catalog documentation for a connector, keyed by category (e.g. "slack") —
   * works before and after the connector is connected: the description, logo,
   * README and every action's input/output schema come from the connector's
   * own code, not from a connected instance or the action registry.
   */
  getConnectorDocs: (category: string) =>
    dataServiceClient.get<ConnectorDocs>(
      `/connector/schema/${encodeURIComponent(category)}?include_readme=true`,
    ),

  /** Update editable fields via POST /connectors/update (config + triggers). */
  updateConnectorConfig: (
    projectId: string,
    connectorId: string,
    _category: string,
    body: { _id?: string; config?: Record<string, unknown>; triggers?: unknown[]; [key: string]: unknown },
  ) =>
    apiClient.put<ProjectConnector>(
      `/api/v1/dataservice/projects/${projectId}/connectors/${connectorId}/config`,
      { ...body, _id: connectorId },
    ),

  getPollingInterval: (projectId: string, connectorId: string) =>
    dataServiceClient.get<ConnectorPollingInterval>(
      `/connectors/${encodeURIComponent(connectorId)}/polling-interval?projectID=${encodeURIComponent(projectId)}`,
    ),

  /** `intervalSeconds: null` resets the connector to its plan's default interval. */
  setPollingInterval: (projectId: string, connectorId: string, intervalSeconds: number | null) =>
    dataServiceClient.put<ConnectorPollingInterval>(
      `/connectors/${encodeURIComponent(connectorId)}/polling-interval?projectID=${encodeURIComponent(projectId)}`,
      { interval_seconds: intervalSeconds },
    ),

  // Data-source connection wizard: the connection form schema for a serviceType /
  // category (fields + hidden defaults + filter patterns), sourced from the catalog.
  getOMConnectionForm: (key: string) =>
    apiClient.get<{
      service_type: string; path?: string; fields: UIFormField[];
      defaults?: Record<string, unknown>; filters?: Array<{ field_id: string; label: string }>;
    }>(
      `/api/v1/dataservice/openmetadata/connection-schema/${encodeURIComponent(key)}`
    ),

  // Full OM-parity catalog: every category (Databases, APIs, Messaging, …) with its
  // serviceTypes — drives the wizard's category grid → type grid.
  getOMCatalog: () =>
    apiClient.get<{ categories: Record<string, OMCatalogCategory> }>(
      `/api/v1/dataservice/openmetadata/catalog`
    ),

  // Delete a connector (cascade: stops workflows, removes from graph DB, deletes from data service)
  deleteConnector: (projectId: string, connectorId: string) =>
    apiClient.delete<{ status: string; message: string; connector_id: string; project_id: string; details: Record<string, any> }>(
      `/api/v1/dataservice/projects/${projectId}/connectors/${connectorId}`,
      { timeout: 120000 } // 2 minutes - cascade delete can take a while
    ),

  // Get ontology edit status (check if editing is allowed)
  getOntologyEditStatus: (projectId: string, connectorId: string) =>
    apiClient.get<{
      can_edit: boolean;
      reason?: string;
      running_workflows: string[];
      edits_used: number;
      edits_max: number;
      edits_remaining: number;
      has_ontology: boolean;
    }>(`/api/v1/dataservice/projects/${projectId}/connectors/${connectorId}/ontology/edit-status`),

  // Update connector ontology
  updateOntology: (projectId: string, connectorId: string, data: {
    nodes: Array<{
      name: string;
      label: string;
      description: string;
      category: string;
      properties: Array<{ name: string; type: string; description: string; required: boolean }>;
      primary_key: string;
      display_name_property: string;
      is_core: boolean;
      source_actions: string[];
      scope?: ConnectorNodeScope;
    }>;
    relationships: Array<{
      name: string;
      label: string;
      description: string;
      from_node: string;
      to_node: string;
      cardinality: string;
      required: boolean;
      semantic_type: string;
    }>;
  }) =>
    apiClient.put<{
      success: boolean;
      message: string;
      connector_id: string;
      project_id: string;
      nodes_count: number;
      relations_count: number;
      edits_used: number;
      edits_max: number;
    }>(`/api/v1/dataservice/projects/${projectId}/connectors/${connectorId}/ontology`, data),
};
